import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, inject, Injector, Input, input, OnDestroy, OnInit, output, viewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { CustomerdbService } from 'src/app/components/services/customer-db/services/customer-db.service';

export interface SelectionEvent {
	name: string;
	id: string;
}

@Component({
	selector: 'mcm-customer-select',
	templateUrl: './customer-selector.component.html',
	styleUrls: ['./customer-selector.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: CustomerSelectorComponent,
			multi: true,
		},
		{
			provide: NG_VALIDATORS,
			useExisting: CustomerSelectorComponent,
			multi: true,
		},
	],
	standalone: false,
})
export class CustomerSelectorComponent implements AfterViewInit, ControlValueAccessor, OnDestroy, OnInit {
	readonly readonly = input<boolean>(false);
	readonly appearance = input<MatFormFieldAppearance>('fill');
	readonly hideHint = input<boolean>(false);
	@Input() initialValue: string = '';
	readonly useSnowID = input<boolean>(false);
	readonly allowWildcard = input<boolean>(false);

	readonly trigger = viewChild(MatAutocompleteTrigger);
	readonly matInput = viewChild(MatInput);

	readonly tenantChanged = output<SelectionEvent>();
	readonly selectionFinalized = output<void>();

	private translate = inject(TranslateService);

	public wildcardOption: SelectionEvent = { id: '*', name: '**' + this.translate.instant('shared.customerSelector.allTenants') + '**' };
	public filteredOptions: SelectionEvent[] = [];
	public optionSelected?: SelectionEvent = undefined;
	public tenantLabel!: string;

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	onChange = (val: any) => {};
	onTouched = () => {};

	loading = false;

	private _onChange: Subject<SelectionEvent> = new Subject<SelectionEvent>();
	private _subscriptions: Subscription[] = [];

	constructor(
		private injector: Injector,
		private customerDb: CustomerdbService
	) {
		// Debouncing subscription
		this._subscriptions.push(
			this._onChange
				.asObservable()
				.pipe(
					filter<SelectionEvent>(Boolean),
					tap(() => (this.loading = true)),
					debounceTime(300),
					tap(() => (this.loading = false)),
					distinctUntilChanged(),
					tap((value: SelectionEvent) => {
						this.optionSelected = value;
						this.onChange(value.id);
						this.tenantChanged.emit(value);
						this.searchCustomers(value.name);
					})
				)
				.subscribe()
		);
	}

	async ngOnInit(): Promise<void> {
		if (this.useSnowID() !== true) {
			this.tenantLabel = this.translate.instant('shared.customerSelector.tenantId');
		} else {
			this.tenantLabel = this.translate.instant('shared.customerSelector.snowId');
		}

		// Update wildcard option with translated text
		this.wildcardOption = {
			id: '*',
			name: '**' + this.translate.instant('shared.customerSelector.allTenants') + '**',
		};
	}

	ngOnDestroy(): void {
		this._subscriptions.forEach((sub) => sub.unsubscribe());
	}

	public get value(): string {
		return this.optionSelected?.id || '';
	}

	ngAfterViewInit() {
		this.trigger()!.panelClosingActions.subscribe(() => {
			const trigger = this.trigger();
			if (trigger!.activeOption) {
				const value = trigger!.activeOption.value;
				this.writeValue(value);
				this.onChange(value);
			}
		});

		// this is needed in order for the mat-form-field to be marked as invalid when the control is invalid
		setTimeout(() => {
			this.matInput()!.ngControl = this.injector.get(NgControl, null)!;
		});
	}

	writeValue(obj: any): void {
		const trigger = this.trigger();
		if (obj && trigger) {
			trigger.writeValue(obj);
			this.optionSelected = obj;
			this.searchCustomers(obj);
		} else if (obj && !trigger) {
			this.initialValue = obj;
			this.optionSelected = obj;
		}
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState?(isDisabled: boolean): void {
		// if controls are not present, they are kind of disabled
		const matInput = this.matInput();
		if (matInput) {
			matInput.disabled = isDisabled;
		}

		const trigger = this.trigger();
		if (trigger) {
			trigger.setDisabledState(isDisabled);
		}
	}

	validate(): { [key: string]: any } {
		return {};
	}

	valueChanged(event: any) {
		this._onChange.next({ id: event.target.value, name: event.target.value });
		if (event.target.value === '*') {
			this.trigger()!.closePanel();
		}
	}

	onOptionSelected(event: any) {
		const value = event.option.value;
		this.optionSelected = value;
		this.onChange(value.id);
		this.tenantChanged.emit(value);
		this.selectionFinalized.emit();
	}

	async searchCustomers(customer: string) {
		this.loading = true;

		try {
			this.filteredOptions = (await this.customerDb.searchCustomer(customer)).map((option) => {
				let displayName = option.name;

				if (option.businessUnitName) {
					displayName += ` [${option.businessUnitName}]`;
				} else if (this.useSnowID()) {
					displayName += ` [${option.snowNumber}]`;
				}

				return { id: option.tenantId, name: displayName };
			});
		} catch (e) {
			if (e instanceof HttpErrorResponse) {
				console.log(e);
			}
		}
		this.loading = false;
	}

	resetSelection() {
		this.optionSelected = undefined;
		const matInput = this.matInput();
		if (matInput) {
			matInput.value = '';
		}
		this.onChange('');
	}

	renderOption(option: SelectionEvent) {
		return `${option.name} | ${option.id}`;
	}
}
